import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import styles from "../static/styles/WeeklyTournamentJoin.module.css";
import calendarIcon from "../static/img/Home/calendarIcon.svg";
import weeklyTournamentBg from "../static/img/WeeklyTournaments/imagePubgWeeklyTournament.avif";

import { trackTournamentSignup } from "../utils/analytics";
import { fetchTournamentById, tournamentEntry } from "../api/tournaments";

import MessagePopup from "../components/MessagePopup";
import ArrowLeft from "../static/img/backArrow.png";

const WeeklyTournamentJoin = ({ isJcUser }) => {
  const [searchParams] = useSearchParams();
  const tournamentId = searchParams.get("tournamentId");

  const [tournamentData, setTournamentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [isEnded, setIsEnded] = useState(false);
  const [makeEntryLoading, setMakeEntryLoading] = useState(false);

  const [popupMessage, setPopupMessage] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const navigate = useNavigate();
  // refs instead of querySelector
  const uidRef = useRef();
  const nickRef = useRef();
  const emailRef = useRef();

  const joinedPlayers = tournamentData?.entries?.length || 0;

  const totalPrize =
    Number(tournamentData?.prize || 0) +
    Number(tournamentData?.second_prize || 0) +
    Number(tournamentData?.third_prize || 0);

  const formatNumber = (num) => {
    if (num >= 1000) return (num / 1000).toFixed(1) + "K";
    return num;
  };

  useEffect(() => {
    const fetchTournamentDetails = async () => {
      if (!tournamentId) {
        setError("Tournament ID is missing");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const data = await fetchTournamentById(tournamentId);

        setTournamentData(data);
        setIsEnded(data.status > 2);
        setError(null);
      } catch (err) {
        setError(err.message || "Failed to load tournament details");
      } finally {
        setLoading(false);
      }
    };

    fetchTournamentDetails();
  }, [tournamentId]);

  useEffect(() => {
    document.title = "Join Tournament | Jazz Esl";
  }, []);

  const entryFeeLabel =
    tournamentData?.entryFee !== null
      ? "FREE"
      : `${tournamentData?.entryFee} Credits`;

  const handleRegister = async () => {
    if (makeEntryLoading) return;

    setMakeEntryLoading(true);

    const uidInput = uidRef.current;
    const nickInput = nickRef.current;
    const emailInput = emailRef.current;

    let hasError = false;

    // UID validation
    if (
      Number(tournamentData?.UIDCheck) === 1 &&
      (!uidInput || !uidInput.value.trim())
    ) {
      uidInput?.classList.add(styles.errorInput);
      hasError = true;
    } else {
      uidInput?.classList.remove(styles.errorInput);
    }

    // Nick validation
    if (!nickInput || !nickInput.value.trim()) {
      nickInput?.classList.add(styles.errorInput);
      hasError = true;
    } else {
      nickInput?.classList.remove(styles.errorInput);
    }

    // Email validation
    if (
      tournamentData?.tourn_type !== "Credits" &&
      (!emailInput || !emailInput.value.trim())
    ) {
      emailInput?.classList.add(styles.errorInput);
      hasError = true;
    } else {
      emailInput?.classList.remove(styles.errorInput);
    }

    if (hasError) {
      setMakeEntryLoading(false);
      return;
    }

    const params = {
      id: tournamentId,
      pubg_id: uidInput?.value.trim(),
      pubg_nick: nickInput.value.trim(),
      userEmail: emailInput?.value.trim(),
    };

    try {
      const response = await tournamentEntry(params);

      if (response?.data?.already_submited) {
        setPopupMessage(
          "You have already submitted. You will receive an SMS with tournament room ID and room password.",
        );
      } else {
        trackTournamentSignup(
          tournamentData.tourn_name,
          tournamentId,
          "Weekly",
        );

        setPopupMessage(
          "You are successfully registered. You will receive an SMS with tournament room ID and room password",
        );
      }
    } catch (error) {
      setPopupMessage(
        error.message || "Failed to register for the tournament.",
      );
    } finally {
      setMakeEntryLoading(false);
      setIsPopupOpen(true);
    }
  };

  if (loading) {
    return <div className={styles.loading}>Loading tournament details...</div>;
  }

  if (error) {
    return <div className={styles.error}>{error}</div>;
  }

  if (!tournamentData) {
    return <div className={styles.error}>No tournament data available</div>;
  }

  return (
    <div className={styles.container}>
      <div className={styles.bgGlow}></div>

      <div className={styles.navigationHeader}>
        <div
          className={styles.navigationBack}
          onClick={() => navigate(-1)}
        >
          <img src={ArrowLeft} alt="<" />
        </div>
        <div className={styles.navigationTitle}>
          <p>Join</p>
          <h2 className={styles.heading}>tournament</h2>
        </div>
      </div>

      {/* Main Card */}
      <div className={styles.tournamentCard}>
        <div className={styles.cardTop}>
          <div className={styles.gameIcon}>
            <img
              src={tournamentData?.banner || weeklyTournamentBg}
              alt={tournamentData?.tourn_name || "Tournament"}
            />
          </div>

          <div>
            <div className={styles.livePlayers}>
              ● LIVE • {joinedPlayers} PLAYERS
            </div>
            <h2>{tournamentData?.tourn_name}</h2>
            <p>Daily Tournament</p>
          </div>
        </div>

        <div className={styles.statsRow}>
          <div>
            {isJcUser ? (
              <span>FREE</span>
            ) : (
              <span>{tournamentData.entryFee != null
                ? `${tournamentData.entryFee}`
                : tournamentData.isEntryFree === false
                  ? "Entry N/A"
                  : "Free"}</span>
            )}
            {isJcUser ? (
              <p>ENTRY</p>
            ) : (
              <p>ENTRY FEE</p>
            )}
          </div>

          <div>
            <span>{joinedPlayers}</span>
            <p>JOINED</p>
          </div>

          <div>
            <span>{formatNumber(totalPrize)}</span>
            <p>PRIZE POOL</p>
          </div>
        </div>
      </div>

      {/* Prize */}
      <h3 className={styles.sectionTitle}>PRIZE DISTRIBUTION</h3>

      <div className={styles.prizeBox}>
        <div className={styles.prizeRow}>
          <div className={styles.prizeLeft}>
            <div className={`${styles.rankBadge} ${styles.gold}`}>1ST</div>
            <span>1ST Place</span>
          </div>
          <strong>{tournamentData?.prize} Credits</strong>
        </div>

        <div className={styles.prizeRow}>
          <div className={styles.prizeLeft}>
            <div className={`${styles.rankBadge} ${styles.silver}`}>2ND</div>
            <span>2ND Place</span>
          </div>
          <strong>{tournamentData?.second_prize} Credits</strong>
        </div>

        <div className={styles.prizeRow}>
          <div className={styles.prizeLeft}>
            <div className={`${styles.rankBadge} ${styles.bronze}`}>3RD</div>
            <span>3RD Place</span>
          </div>
          <strong>{tournamentData?.third_prize} Credits</strong>
        </div>
      </div>

      {/* Rules */}
      <div className={styles.rulesBox}>
        <h4>📋 QUICK RULES</h4>
        <ul>
          <li>Best of 1 - Solo mode - Mobile only</li>
          <li>Top 3 finishers win credits</li>
        </ul>
      </div>

      {/* Form */}
      <div className={styles.formSection}>
        <input
          ref={uidRef}
          className={styles.input}
          placeholder="Enter Your UID"
        />
        <input
          ref={nickRef}
          className={styles.input}
          placeholder="Enter Your NICK"
        />
        {/* <input
          ref={emailRef}
          className={styles.input}
          placeholder="Enter Your Email"
        /> */}

        <button
          className={styles.registerButton}
          onClick={handleRegister}
          disabled={makeEntryLoading || isEnded || tournamentData.status === 2}
        >
          {makeEntryLoading
            ? "Please wait..."
            : tournamentData.status >= 2
              ? "Registrations Closed"
              : "Register"}
        </button>

        {/* <p className={styles.footerText}>
          Cancel anytime before match starts · 50% refund
        </p> */}
      </div>

      <MessagePopup
        isOpen={isPopupOpen}
        message={popupMessage}
        onClose={() => {
          setIsPopupOpen(false);
          navigate("/");
        }}
      />
    </div>
  );
};

export default WeeklyTournamentJoin;
